import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface Submission {
  studentId: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  submitted: boolean
  submissionId: string | null
}

interface HomeworkData {
  id: string
  subject: string
  title: string
  dueDate: string
}

type FilterTab = 'all' | 'submitted' | 'pending'

export function HomeworkDetailPage() {
  const { homeworkId } = useParams()
  const navigate = useNavigate()
  const [hw, setHw] = useState<HomeworkData | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submittedCount, setSubmittedCount] = useState(0)
  const [totalStudents, setTotalStudents] = useState(0)
  const [filter, setFilter] = useState<FilterTab>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [selectedForNotify, setSelectedForNotify] = useState<Set<string>>(new Set())
  const [isSending, setIsSending] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!homeworkId) return
    fetch(apiUrl(`/api/homework/${homeworkId}/submissions`), { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setHw(data.data.homework)
          setSubmissions(data.data.submissions)
          setSubmittedCount(data.data.submittedCount)
          setTotalStudents(data.data.totalStudents)
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [homeworkId])

  async function toggleSubmission(s: Submission) {
    if (s.submitted && s.submissionId) {
      // Unmark
      await fetch(apiUrl(`/api/homework/submissions/${s.submissionId}`), {
        method: 'DELETE',
        credentials: 'include',
      })
      setSubmissions((prev) =>
        prev.map((sub) =>
          sub.studentId === s.studentId
            ? { ...sub, submitted: false, submissionId: null }
            : sub,
        ),
      )
      setSubmittedCount((c) => c - 1)
    } else {
      // Mark submitted
      const res = await fetch(apiUrl(`/api/homework/${homeworkId}/submissions`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId: s.studentId }),
      })
      const data = await res.json()
      if (data.success) {
        setSubmissions((prev) =>
          prev.map((sub) =>
            sub.studentId === s.studentId
              ? { ...sub, submitted: true, submissionId: data.data?.id ?? 'temp' }
              : sub,
          ),
        )
        setSubmittedCount((c) => c + 1)
      }
    }
  }

  const filtered = submissions.filter((s) => {
    if (filter === 'submitted') return s.submitted
    if (filter === 'pending') return !s.submitted
    return true
  })

  const pendingList = submissions.filter((s) => !s.submitted)

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังโหลด...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate('/homework')} className="text-gray-600">⟨</button>
          <h1 className="truncate text-base font-bold text-gray-900">
            {hw?.subject}: {hw?.title}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-3">
        <p className="mb-3 text-sm text-gray-500">
          ส่งแล้ว {submittedCount}/{totalStudents} · กำหนดส่ง {hw?.dueDate}
        </p>

        {/* Filter tabs */}
        <div className="mb-3 flex gap-2">
          {([
            { key: 'all' as const, label: 'ทั้งหมด' },
            { key: 'submitted' as const, label: 'ส่งแล้ว ✓' },
            { key: 'pending' as const, label: 'ยังไม่ส่ง ⏳' },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                filter === tab.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 shadow-sm'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Submission list */}
        <div className="space-y-1">
          {filtered.map((s) => (
            <button
              key={s.studentId}
              onClick={() => toggleSubmission(s)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                s.submitted ? 'bg-green-50' : 'bg-white shadow-sm'
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded text-xs ${
                  s.submitted
                    ? 'bg-green-500 text-white'
                    : 'border-2 border-gray-300'
                }`}
              >
                {s.submitted && '✓'}
              </span>
              <span className={s.submitted ? 'text-gray-500' : 'text-gray-900'}>
                {s.studentNumber} · {s.prefix}{s.fullName}
                {s.nickname && <span className="text-gray-400"> ({s.nickname})</span>}
              </span>
            </button>
          ))}
        </div>

        {/* Notify section */}
        {pendingList.length > 0 && (
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={selectedForNotify.size === pendingList.length && pendingList.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedForNotify(new Set(pendingList.map((s) => s.studentId)))
                  } else {
                    setSelectedForNotify(new Set())
                  }
                }}
                className="rounded"
              />
              เลือกทั้งหมดที่ยังไม่ส่ง ({pendingList.length})
            </label>

            {selectedForNotify.size > 0 && (
              <button
                onClick={async () => {
                  setIsSending(true)
                  try {
                    const res = await fetch(apiUrl(`/api/notifications/homework/${homeworkId}/notify`), {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ studentIds: [...selectedForNotify] }),
                    })
                    const data = await res.json()
                    setToast(data.message || 'ส่งแจ้งเตือนแล้ว')
                    setSelectedForNotify(new Set())
                  } catch {
                    setToast('เกิดข้อผิดพลาด')
                  }
                  setIsSending(false)
                }}
                disabled={isSending}
                className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {isSending ? 'กำลังส่ง...' : `📣 แจ้งเตือน (${selectedForNotify.size} คน)`}
              </button>
            )}
          </div>
        )}

        <p className="mt-3 text-center text-xs text-gray-400">
          แตะชื่อเพื่อเปลี่ยนสถานะ "ส่งแล้ว"
        </p>
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="homework" />
    </div>
  )
}
